import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { DeleteIcon, FamilyTwoIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { activateHostFamilies } from '../../../api/host-families/activate-families'
import { deactivateHostFamilies } from '../../../api/host-families/deactivate-families'
import { deleteHostFamilies } from '../../../api/host-families/delete-host-family'
import { Paths } from '../../../lib/constants/path'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import {
  DeactivationReasonEnum,
  OL_HOST_FAMILY_DEACTIVATION_REASONS
} from '../../../lib/types/enums/staff/deactivation-reason.enum'
import { HostFamilyInput } from '../../../lib/types/models/host-family/host-family-input'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { t } from '../../../locale/utils'
import DateChip from '../../elements/date-chip'
import { StatusToggle } from '../../elements/status-toggle'
import { HostFamilyDeactivationForm } from '../ols/host-family/host-family-deactivation-form'

const HostFamilyFormHeader = () => {
  const id = useParams<{ id: string }>().id
  const { navigate } = useNavigator()

  const { modalState, toggleModal, closeModel } = useModalManager(['delete', 'deactivate'])

  const form = useFormContext<HostFamilyInput>()

  const { mutateAsync: activateHostFamiliesMutation } = useMutation({
    mutationFn: activateHostFamilies,
    onError: (error) => {
      showAxiosError(error)
      form.setValue('deactivated', true)
    },
    onSuccess: () => {
      toast.success('Host family activated')
      form.setValue('deactivated', false)
      form.setValue('deactivationReason', null)
      closeModel('deactivate')
    }
  })

  const { mutateAsync: deactivateHostFamiliesMutation } = useMutation({
    mutationFn: deactivateHostFamilies,
    onError: (error) => {
      showAxiosError(error)
      form.setValue('deactivated', false)
    },
    onSuccess: (_, { deactivationReason }) => {
      toast.success('Host family deactivated')
      form.setValue('deactivated', true)
      form.setValue('deactivationReason', deactivationReason)
      closeModel('deactivate')
    }
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: deleteHostFamilies,
    onError: showAxiosError
  })

  const deactivated = form.watch('deactivated')
  // deactivated by LEC as not suitable: only LEC can reactivate it (enforced by the backend)
  const isLockedByLec =
    deactivated && form.watch('deactivationReason') === DeactivationReasonEnum.NOT_SUITABLE

  if (!id || isNaN(+id)) return null

  return (
    <div className="flex items-center w-full justify-between">
      <div className="flex items-center gap-3">
        <DateChip date={form.formState.defaultValues?.createdAt ?? new Date()} label={'Created'} />
        <DateChip date={form.formState.defaultValues?.updatedAt ?? new Date()} label={'Updated'} />
      </div>
      <div className="flex items-center gap-2">
        {isLockedByLec && (
          <Typography variant="small-semibold" color={tailwindColors.red.DEFAULT}>
            {t('host.family.forms.deactivation.locked.by.lec')}
          </Typography>
        )}
        <StatusToggle
          isActive={!deactivated}
          disabled={isLockedByLec}
          onToggle={(value) => {
            if (value) {
              activateHostFamiliesMutation([+id])
            } else {
              form.setValue('deactivated', true)
              toggleModal('deactivate')
            }
          }}
        />
        <Button
          variant="ghost"
          type="button"
          icon={DeleteIcon}
          iconColor={tailwindColors.red.DEFAULT}
          label="Delete"
          className="w-32 text-red bg-red-light border-red-light hover:bg-red-hover hover:border-red"
          onClick={() => {
            toggleModal('delete')
          }}
        />
      </div>

      <Modal
        actionButtonsOptions={{
          confirmText: 'Confirm',
          cancelText: 'Cancel',
          onConfirm: async () => {
            await mutateAsync([+id])
            toggleModal('delete')
            navigate(Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_LIST_PAGE, { replace: true })
          }
        }}
        onCancel={() => toggleModal('delete')}
        isOpen={modalState.delete}
        title={'Are you sure you want to delete this host family?'}
        icon={DeleteIcon}
        isLoading={isPending}
        variant={'destructive'}
      />

      <Modal
        isOpen={modalState.deactivate}
        title={t('host.family.forms.deactivation.label')}
        icon={FamilyTwoIcon}
        hideActions
        onCancel={() => {
          form.setValue('deactivated', false)
          closeModel('deactivate')
        }}
      >
        <HostFamilyDeactivationForm
          defaultValues={{ ids: [+id] }}
          reasons={OL_HOST_FAMILY_DEACTIVATION_REASONS}
          buttonsConfig={modalButtonConfig({
            confirmText: t('host.family.forms.deactivation.submit.label'),
            cancelText: t('host.family.forms.deactivation.cancel.label'),
            onCancel: () => {
              form.setValue('deactivated', false)
              closeModel('deactivate')
            },
            onConfirm: deactivateHostFamiliesMutation
          })}
        />
      </Modal>
    </div>
  )
}

export default HostFamilyFormHeader
